import useInfiniteService from '@/data/hook/useInfiniteService';
import JsonApiResponse, { Meta } from '@/models/JsonApiResponse';
import { BaseFilters, ServiceReturn } from '@/models/Service';
import { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchInput, {
  CustomInputProps,
  SearchInputMode,
  SearchOption,
} from './SearchInput';
import { BaseInputProps } from './BaseInput';
import { Model as BaseModel } from '@/models/Model';

export type CreateOptionFn<T> = (item: T, number: number) => SearchOption<T>;

export type InfiniteSearchInputProps<
  Filter extends BaseFilters,
  Model,
> = BaseInputProps & {
  selectedItem?: Model | null;
  service: (filters?: Filter) => ServiceReturn<Model>;
  filters?: Filter;
  displayName: (option: Model) => string;
  onSelect(item: Model): void;
  onDeselect?(): void;
  blockDeselect?: boolean;
  input?: Omit<CustomInputProps, 'onChange' | 'value'>;
  options?: Omit<UseInfiniteQueryOptions<JsonApiResponse<Model>>, 'queryKey'>;
  inputIcon?: JSX.Element;
  loading?: boolean;
  onGetMeta?: (meta: Meta) => void;
  createOption?: CreateOptionFn<Model>;
  clearOnSelect?: boolean;
  mode?: SearchInputMode;
};

export default function InfiniteSearchInput<
  Filter extends BaseFilters,
  Model extends BaseModel,
>({
  selectedItem,
  service,
  displayName,
  blockDeselect,
  onSelect,
  onDeselect,
  input,
  filters,
  options: queryOptions,
  inputIcon,
  loading,
  onGetMeta,
  createOption = createDefaultOption,
  clearOnSelect,
  mode = 'single',
}: InfiniteSearchInputProps<Filter, Model>) {
  const [searchValue, setSearchValue] = useState<string>();
  const [search, setSearch] = useState<string>();

  const {
    meta,
    results: items,
    isInitialLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...queryOptions,
    keepPreviousData: true,
    ...service({
      ...filters,
      search,
    } as Filter)._ctx.infinity,
  });

  useEffect(() => {
    if (selectedItem) setSearchValue(displayName(selectedItem));
    else if (selectedItem === null) setSearchValue(undefined);
  }, [displayName, selectedItem]);

  useEffect(() => {
    if (meta) {
      onGetMeta?.(meta);
    }
  }, [meta, onGetMeta]);

  const options = items.map<SearchOption<Model>>(createOption);

  const memoedDebounce = useMemo(
    () => debounce(search => setSearch(search), 500),
    [setSearch],
  );

  const debouncedSearch = useCallback(
    (search?: string) => {
      memoedDebounce(search);
    },
    [memoedDebounce],
  );

  const handleSearch: React.ChangeEventHandler<HTMLInputElement> = e => {
    const value = e.target.value;

    setSearchValue(value ?? undefined);
    debouncedSearch(value ?? undefined);
  };

  const handleSelectItem = (item: Model) => {
    onSelect(item);
    setSearchValue(clearOnSelect ? undefined : displayName(item));
    setSearch(undefined);
  };

  const handleDeselect = () => {
    setSearch(undefined);
    setSearchValue(undefined);
    onDeselect?.();
  };

  return (
    <SearchInput
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      options={options}
      displayName={displayName}
      onSearch={handleSearch}
      onSelect={handleSelectItem}
      value={searchValue ?? ''}
      isLoading={loading || isInitialLoading}
      isSearching={isInitialLoading}
      hasItemSelected={!!selectedItem || !!searchValue}
      onDeselect={blockDeselect ? undefined : handleDeselect}
      input={input}
      inputIcon={inputIcon}
      mode={mode}
    />
  );
}

const createDefaultOption = <T,>(item: T, index: number): SearchOption<T> => ({
  value: item,
  key: index,
});
